import { getAccessToken } from "@/core/components/auth/services/cookies";
import { fetchWithTokenRefresh } from "@/core/utils/http/fetch-with-token-refresh";

/**
 * Porta web da Bruna para o chat do WOS (PED-14548).
 *
 * O chat não fala com o agente usando o token do WOS. Ele troca a sessão do WOS
 * (`POST /agent/session` no api-gateway, PED-14551/PED-14550) por um token restrito de
 * audiência `bruna-wos-chat`, que vai como `Authorization`. O token do WOS vai só no
 * header `X-WOS-Token`, que o manager repassa à Lia no turno (nunca no corpo).
 *
 * Tudo passa pelo api-gateway: `/agent/session` (facilities-manager-api) e
 * `/api/web/agent/stream` (a porta web da Bruna, pública no gateway, que só confere o
 * `Bearer`; o token restrito quem verifica é a Bruna).
 */

/** Resposta de `POST /agent/session` (facilities-manager-api). */
interface AgentSessionResponse {
  accessToken: string;
  expiresAt: string;
}

/** Pede um token novo quando faltar menos que isto para o atual expirar. */
export const SESSION_RENEW_BEFORE_MS = 60_000;

let session: { token: string; expiresAtMs: number } | null = null;
let pendingSession: Promise<string> | null = null;

function gatewayUrl(): string {
  return import.meta.env.VITE_API_GATEWAY_URL || "";
}

/** O chat do WOS fala com a Bruna neste ambiente (`VITE_BRUNA_WOS_CHAT=true`). */
export function brunaWosChatEnabled(): boolean {
  return import.meta.env.VITE_BRUNA_WOS_CHAT === "true";
}

/** Esquece o token restrito (logout, troca de empresa, testes). */
export function clearBrunaSession(): void {
  session = null;
  pendingSession = null;
}

async function requestSession(): Promise<string> {
  const response = await fetchWithTokenRefresh(
    `${gatewayUrl()}/agent/session`,
    {
      method: "POST",
    },
  );
  if (!response.ok) {
    throw new Error(`Bruna session HTTP error: ${response.status}`);
  }
  const body = (await response.json()) as AgentSessionResponse;
  const expiresAtMs = Date.parse(body.expiresAt);
  if (!body.accessToken || Number.isNaN(expiresAtMs)) {
    throw new Error("Bruna session invalid");
  }
  session = { token: body.accessToken, expiresAtMs };
  return body.accessToken;
}

/**
 * O token restrito da sessão, renovado antes de expirar. Chamadas simultâneas
 * compartilham a mesma requisição.
 */
export async function brunaSessionToken(force = false): Promise<string> {
  if (
    !force &&
    session &&
    session.expiresAtMs - Date.now() > SESSION_RENEW_BEFORE_MS
  ) {
    return session.token;
  }
  if (!pendingSession) {
    pendingSession = requestSession().finally(() => {
      pendingSession = null;
    });
  }
  return pendingSession;
}

function postStream(
  token: string,
  question: string,
  signal?: AbortSignal,
): Promise<Response> {
  return fetch(`${gatewayUrl()}/api/web/agent/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-WOS-Token": getAccessToken() || "",
    },
    body: JSON.stringify({ question }),
    signal,
  });
}

/**
 * Abre o stream de um turno. Um 401 (token restrito expirado ou recusado) pede uma
 * sessão nova e tenta uma vez mais.
 */
export async function openBrunaStream(
  question: string,
  signal?: AbortSignal,
): Promise<Response> {
  const response = await postStream(
    await brunaSessionToken(),
    question,
    signal,
  );
  if (response.status !== 401) return response;
  response.body?.cancel().catch(() => {});
  return postStream(await brunaSessionToken(true), question, signal);
}

export interface BrunaStreamEvent {
  event: string;
  data: Record<string, unknown>;
}

/**
 * Separa os eventos SSE completos do buffer (o servidor usa `\r\n`) e devolve o
 * fragmento incompleto para o próximo chunk.
 */
export function takeSseEvents(buffer: string): {
  events: BrunaStreamEvent[];
  rest: string;
} {
  // Um `\r` no fim do chunk pode ser a metade de um `\r\n`: fica no buffer.
  const parts = buffer.replace(/\r\n|\r(?!$)/g, "\n").split("\n\n");
  const rest = parts.pop() ?? "";
  const events: BrunaStreamEvent[] = [];
  for (const part of parts) {
    let event = "message";
    const data: string[] = [];
    for (const line of part.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
    }
    if (!data.length) continue;
    try {
      const parsed: unknown = JSON.parse(data.join("\n"));
      if (parsed && typeof parsed === "object") {
        events.push({ event, data: parsed as Record<string, unknown> });
      }
    } catch {
      console.error("Failed to parse Bruna SSE event:", event);
    }
  }
  return { events, rest };
}

/** O fim de um turno: a resposta e os blocks crus do `done`, ou `failed` num `error`. */
export interface BrunaTurn {
  answer: string;
  blocks: unknown;
  failed: boolean;
}

/**
 * Lê o stream até o `done` ou o `error`. `meta` e `update` só avisam que o servidor
 * está ativo (`onActivity`): a resposta chega inteira no `done`.
 */
export async function readBrunaTurn(
  response: Response,
  onActivity: () => void = () => {},
): Promise<BrunaTurn> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error("Stream not available");
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = takeSseEvents(buffer);
      buffer = rest;
      for (const { event, data } of events) {
        onActivity();
        if (event === "done") {
          const answer = typeof data.answer === "string" ? data.answer : "";
          return { answer, blocks: data.blocks, failed: false };
        }
        if (event === "error")
          return { answer: "", blocks: undefined, failed: true };
      }
    }
    return { answer: "", blocks: undefined, failed: true };
  } finally {
    // O turno acabou (ou falhou): solta a conexão sem esperar o servidor fechá-la.
    reader.cancel().catch(() => {});
  }
}
